import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { TextField } from "$components/rhf-fields";
import {
  useConvertFromPenceFactory,
  useConvertFromPoundsFactory,
} from "$routes/_lib/use-convert-currency";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { number, object, string } from "yup";
import useCurrencyFormatter from "../../hooks/useCurrencyFormatter";
import useRefundInvoice from "./hooks/useRefundInvoice";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
  },
  totalAmount: {
    marginTop: "4px",
    fontSize: "24px",
    fontWeight: 700,
  },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 400,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
}));

export const REASON = "reason";
export const AMOUNT_TO_REFUND = "amountToRefund";

const useRefundSchema = ({ totalRefundableInPence }) => {
  const penceToPounds = useConvertFromPenceFactory();

  return useMemo(
    () =>
      object().shape({
        [REASON]: string().required(),
        [AMOUNT_TO_REFUND]: number()
          .required()
          .positive()
          .max(penceToPounds(totalRefundableInPence)),
      }),
    [totalRefundableInPence, penceToPounds],
  );
};

type FormState = {
  reason: string;
  amountToRefund: number;
};

export const RefundInvoiceModal: FC<{
  invoiceId: string;
  open: boolean;
  onClose: () => void;
  currency: string;
  totalRefundableInPence: number;
}> = ({ invoiceId, open, onClose, currency, totalRefundableInPence }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { refundInvoice } = useRefundInvoice(invoiceId);
  const { currencyFormatter } = useCurrencyFormatter();

  const penceToPounds = useConvertFromPenceFactory();
  const poundsToPence = useConvertFromPoundsFactory();
  const [busy, setBusy] = useState(false);

  const handleErrorMessage = (value) =>
    value?.id ? formatMessage(value) : value;

  const handleRefund = async (values: FormState) => {
    setBusy(true);
    try {
      await refundInvoice({
        ...values,
        amountToRefund: poundsToPence(values.amountToRefund),
      });
    } finally {
      onClose();
    }
  };

  const amountLabel = formatMessage({
    id: "invoicing.refund.amount.placeholder",
    defaultMessage: "Amount to Refund",
  });

  const reasonLabel = formatMessage({
    id: "invoicing.refund.reason.label",
    defaultMessage: "Reason for refunding the invoice",
  });

  const reasonPlaceholder = formatMessage({
    id: "invoicing.refund.reason.placeholder",
    defaultMessage: "Reason for refunding the invoice",
  });

  const initialValues = useMemo(
    () => ({
      [REASON]: undefined,
      [AMOUNT_TO_REFUND]: penceToPounds(totalRefundableInPence),
    }),
    [totalRefundableInPence, penceToPounds],
  );

  const formMethods = useForm<FormState>({
    defaultValues: initialValues,
    resolver: yupResolver(useRefundSchema({ totalRefundableInPence })),
    mode: "onTouched",
  });

  return (
    <DialogContainer open={open} className={classes.modal}>
      <FormProvider {...formMethods}>
        <form
          name="refund-invoice-form"
          onSubmit={formMethods.handleSubmit(handleRefund)}
        >
          <DialogHeader title="">
            <Box display="flex" alignItems="center">
              <img
                src={warningIcon}
                alt="warning"
                className={classes.warningIcon}
              />
              <Typography gutterBottom variant="h5">
                <FormattedMessage
                  id="invoicing.refund.confirmation.title"
                  defaultMessage="You are about to refund this invoice"
                />
              </Typography>
            </Box>
          </DialogHeader>
          <DialogBody>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item xs={4}>
                <FormattedMessage
                  id="invoicing.refund.totalAmountLabel"
                  defaultMessage="Amount Paid ({currency})"
                  values={{
                    currency,
                  }}
                />
                <div className={classes.totalAmount}>
                  {currencyFormatter(totalRefundableInPence)}
                </div>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  inputProps={{
                    min: "1",
                    step: ".01",
                  }}
                  type="number"
                  name={AMOUNT_TO_REFUND}
                  label={amountLabel}
                  disabled={busy}
                  helperText={
                    formMethods.formState.touchedFields[AMOUNT_TO_REFUND] &&
                    handleErrorMessage(
                      formMethods.formState.errors[AMOUNT_TO_REFUND],
                    )
                  }
                  data-testid={"invoicing.refund.amountToRefund"}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name={REASON}
                  label={reasonLabel}
                  placeholder={reasonPlaceholder}
                  required
                  multiline
                  minRows={5}
                  fullWidth
                  data-testid={"invoicing.refund.reason"}
                  disabled={busy}
                  helperText={
                    formMethods.formState.touchedFields[REASON] &&
                    handleErrorMessage(formMethods.formState.errors[REASON])
                  }
                />
              </Grid>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={() => onClose()} disabled={busy}>
              <FormattedMessage
                id="invoicing.refund.confirmation.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              color="primary"
              data-testid="invoicing.refund.confirmation.accept"
              startIcon={<CheckIcon />}
              loading={busy}
              disabled={
                formMethods.formState.isSubmitting ||
                !formMethods.formState.isValid
              }
              type="submit"
            >
              <FormattedMessage
                id="invoicing.refund.confirmation.accept"
                defaultMessage="Refund"
              />
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};

export default RefundInvoiceModal;
